import { GAMES_COUNTED, GAMES_MAX, WALL_GAME_POINTS } from '@league/shared/scoring';
import { useEffect, useRef, useState } from 'react';

import { addGame, emptyGames, emptyGamesMessage, gridProblems, gridTotals, hasAnyEntry, isWallGame, removeGame, type Game } from './grid';

/** One player's column. */
export interface GridColumn {
  playerId: string;
  /** The heading — short, because four of them share a phone's width. */
  label: string;
  /** For the cell's screen-reader name. */
  fullName: string;
  /** Her own column, on the player's card — marked "you", as her card always did. */
  isYou?: boolean;
}

/**
 * THE SCORE CARD AS A GRID — the director's sheet and, since 2026-10-04, the player's card too
 * (owner: players get "the same score card functionality as the directors").
 *
 * ⚠ THE SCREEN IS THE CARD. Owner, 2026-09-19: "I can't see the score for each person, for each
 * game. I liked the previous table view better."
 *
 * This replaced a stack of per-game cards that could hold exactly one number each, because the
 * state behind them could hold exactly one number each. That shape was chosen to save keystrokes
 * on a phone, and it cost the thing that actually matters: she is TRANSCRIBING. The sheet in her
 * hand (`apps/api/src/print/ScoreSheet.tsx`, which this platform prints) is a grid of every player
 * against every game, so any other arrangement makes her hold a position in two documents at once
 * and check one number at a time.
 *
 * A grid she can read straight across is also the only version where a typo is visible: a 250 in a
 * row of 25s is obvious here and invisible in a stack of cards.
 */
export function ScoreGrid({
  columns,
  games,
  onChange,
  mode,
  disabled,
  doubtful = new Set(),
  readButEmpty = [],
}: {
  columns: GridColumn[];
  games: Game[];
  /** Every edit, as an update to the rows. The screen marks itself unsaved. */
  onChange: (update: (current: Game[]) => Game[]) => void;
  mode: 'social' | 'tournament' | undefined;
  disabled: boolean;
  /** `playerId:gameNumber` cells a photo's reading was unsure of. */
  doubtful?: Set<string>;
  /** Games the card has writing in that are empty here. */
  readButEmpty?: number[];
}) {
  const totals = gridTotals(games, columns, mode);
  const problems = gridProblems(games, columns);
  const empty = emptyGames(games);
  /** The box she is typing in, as `playerId:gameNumber` — where the ± button shows. */
  const [focused, setFocused] = useState<string | null>(null);

  const canAddGame = Math.max(0, ...games.map(g => g.gameNumber)) < GAMES_MAX;

  return (
    <fieldset disabled={disabled} className="min-w-0">
      {/* One line — owner, 2026-10-04: the grid, the number pad and the totals explain the rest. */}
      <p className="text-[14px] text-ink-2">{mode === 'tournament' ? 'Competitive scoring' : mode === 'social' ? 'Social scoring' : 'Scoring'}</p>

      {/*
        ⚠ THE PLAYERS ARE THE COLUMNS, EVERYWHERE. Owner's ruling, 2026-09-19: "put the player as
        the columns, as that number doesn't change. If groups get 8 games, they would have to
        scroll on the mobile phone."

        That is the whole argument, and it is better than the one it replaced. A table seats four
        (five with a floater) and ALWAYS WILL — it is a card table. Games run from four to eight
        and are added one at a time during the session. So the fixed quantity belongs on the axis
        that cannot grow, and the growing one belongs where growth is free: DOWN, where every
        screen already scrolls.

        The totals are a FOOTER ROW rather than a right-hand column, which is where a column of
        numbers puts its total anyway.
      */}
      {/*
        ⚠ 34rem IS SHARED — owner, 2026-09-19: "make that and the save button as wide as the table."
        The grid, the "+ Game" button and the director's sticky action bar all carry
        `max-w-[34rem]`, so they line up as one column. If it changes, change it everywhere.
      */}
      <table className="mt-3 w-full max-w-[34rem] table-fixed border-separate border-spacing-0">
        <thead>
          <tr>
            {/*
              THE REMOVE COLUMN — owner, 2026-10-14: its own column, unmarked, left of the game number,
              outside the card's border so the card itself is unchanged. Not drawn at all when the
              card cannot be changed.
            */}
            {disabled ? null : (
              <th scope="col" className="w-7 p-0">
                <span className="sr-only">Remove</span>
              </th>
            )}
            <th
              scope="col"
              className="w-12 rounded-tl-md border border-light bg-sand px-1 py-2 text-center align-bottom text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-2"
            >
              {/* ink-2, not ink-3: these sit on sand, where ink-3 fails contrast (axe, 2026-10-04). */}
              Game
            </th>
            {columns.map((column, index) => (
              <th
                key={column.playerId}
                scope="col"
                className={`border-y border-r border-light bg-sand px-0.5 py-2 text-center align-bottom ${index === columns.length - 1 ? 'rounded-tr-md' : ''}`}
              >
                {/*
                  ⚠ NO SEAT — owner, 2026-10-03: "Brooke is east, but that was only for the first
                  game." Seats move round the table game by game, so a wind under a name was true
                  for one row of this grid and wrong for the rest.
                */}
                {/* Wraps rather than cuts — "Mary Ann" on two lines beats "Mary…" (owner, 2026-10-04). */}
                <span className="block text-[13px] font-medium leading-tight text-ink [overflow-wrap:anywhere]">{column.label}</span>
                {/* Not grey: ink-3 is not certified on the sand header. */}
                {column.isYou ? <span className="block text-[11px] leading-tight text-ink-2">you</span> : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {games.map((game) => {
            const wall = isWallGame(game, columns);
            return (
              <tr key={game.gameNumber}>
                {disabled ? null : (
                  <td className="p-0 text-center align-middle">
                    {/*
                      REMOVING A GAME IS HER CHOICE (owner, 2026-10-14), and the games after it move
                      up one. A game with scores asks first; an empty one just goes. A red circle with
                      a white X, drawn rather than typed: a bare "×" by the number read as "1×, 2×".
                      The 32px button is the tap target around the 20px circle.
                    */}
                    <button
                      type="button"
                      aria-label={`Remove game ${game.gameNumber}`}
                      onClick={() => {
                        if (hasAnyEntry(game) && !window.confirm(`Remove game ${game.gameNumber} and its scores?`)) return;
                        onChange((current) => removeGame(current, game.gameNumber));
                      }}
                      className="group -mx-0.5 inline-flex size-8 items-center justify-center rounded-full"
                    >
                      <span className="flex size-5 items-center justify-center rounded-full bg-signal text-on-signal group-hover:bg-signal-hover">
                        <svg viewBox="0 0 10 10" className="size-2.5" aria-hidden="true">
                          <path d="M2 2 L8 8 M8 2 L2 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                      </span>
                    </button>
                  </td>
                )}
                <th scope="row" className="border-x border-b border-light bg-paper px-1 py-1 text-center font-normal">
                  <span className="tabular block text-[14px] font-semibold text-ink">{game.gameNumber}</span>
                  <button
                    type="button"
                    aria-pressed={wall}
                    aria-label={`Game ${game.gameNumber} was a wall game`}
                    onClick={() => {
                      onChange((current) =>
                        current.map((g) =>
                          g.gameNumber === game.gameNumber
                            ? {
                                ...g,
                                saved: undefined,
                                points: wall
                                  ? {}
                                  : Object.fromEntries(columns.map((c) => [c.playerId, String(WALL_GAME_POINTS)])),
                              }
                            : g,
                        ),
                      );
                    }}
                    className={[
                      'rounded-full px-1.5 py-px text-[11px] leading-none',
                      wall ? 'bg-deep font-medium text-on-deep' : 'text-ink-3',
                    ].join(' ')}
                  >
                    Wall
                  </button>
                </th>

                {columns.map((column) => {
                  const key = `${column.playerId}:${game.gameNumber}`;
                  const raw = game.points[column.playerId] ?? '';
                  const setCell = (value: string) =>
                    onChange((current) =>
                      current.map((g) =>
                        g.gameNumber === game.gameNumber ? { ...g, points: { ...g.points, [column.playerId]: value } } : g,
                      ),
                    );
                  return (
                  <td key={column.playerId} className="relative border-b border-r border-light bg-paper p-0 text-center">
                    <input
                      type="text"
                      /*
                        THE NUMBER PAD, EVERYWHERE — and a ± for the minus it lacks. Owner,
                        2026-10-15: "-10 is a possibility", and the iPhone number pad has no minus
                        key. Competitive used the full keyboard for this; the ± replaces that, so
                        every box opens the fast pad.
                      */
                      inputMode="numeric"
                      value={raw}
                      onChange={(event) => setCell(event.target.value)}
                      onFocus={() => setFocused(key)}
                      onBlur={(event) => {
                        if (!(event.relatedTarget instanceof HTMLElement && event.relatedTarget.dataset.sign === key)) setFocused(null);
                      }}
                      aria-label={`${column.fullName}, game ${game.gameNumber}`}
                      /*
                        ⚠ THE FOCUS OUTLINE STAYS. `focus:outline-none` was removing the 2px navy
                        ring the stylesheet sets at 12.55:1 — on a grid of identical boxes, which
                        is precisely where somebody tabbing needs to know which cell they are in.
                        The tint is an ADDITION to the ring, not a replacement for it.
                      */
                      className={`tabular h-12 w-full text-center text-[16px] focus:bg-tint-sage disabled:bg-sand disabled:text-ink-2 ${focused === key && !disabled ? 'pl-7 pr-1' : ''} ${doubtful.has(key) ? 'bg-tint-rust' : 'bg-transparent'}`}
                    />
                    {focused === key && !disabled ? (
                      <SignButton
                        signKey={key}
                        negative={raw.trim().startsWith('-')}
                        label={column.fullName}
                        gameNumber={game.gameNumber}
                        onFlip={() => setCell(flipSign(raw))}
                        onLeave={() => setFocused(null)}
                      />
                    ) : null}
                  </td>
                  );
                })}
              </tr>
            );
          })}

          <tr>
            {disabled ? null : <td className="p-0" />}
            <th
              scope="row"
              className="whitespace-nowrap rounded-bl-md border-x border-b border-light bg-sand px-1.5 py-2 text-left text-[11px] font-semibold uppercase leading-tight text-ink-2"
            >
              Best {GAMES_COUNTED}
            </th>
            {columns.map((column, index) => (
              <td
                key={column.playerId}
                className={`tabular border-b border-r border-light bg-sand px-1 py-2 text-center text-[17px] font-semibold ${index === columns.length - 1 ? 'rounded-br-md' : ''}`}
              >
                {totals.get(column.playerId)?.points ?? 0}
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      {/*
        Which games actually counted. Once a table plays more than four, "why is my 25 not in
        there" is the first thing anybody asks, and a total that cannot answer it is one she has
        to defend from memory.
      */}
      {games.filter(hasAnyEntry).length > GAMES_COUNTED ? (
        <ul className="mt-2 flex flex-col gap-0.5">
          {columns.map((column) => {
            const total = totals.get(column.playerId);
            if (!total || total.counted.length === 0) return null;
            return (
              <li key={column.playerId} className="text-[12px] text-ink-3">
                {column.label}: games {total.counted.join(', ')} count
              </li>
            );
          })}
        </ul>
      ) : null}

      {/*
        ⚠ THE RULE IS ENFORCED BY SAYING SO, NOT BY REFUSING THE KEYSTROKE.

        Owner's ruling, 2026-09-19: one player scores per game, unless it is a wall game and
        everybody records ten. The grid still lets her type anywhere — that is what makes it match
        the card — so a second score in a row is caught here and NAMED, and Save is held until it
        is settled. The earlier screen enforced it by silently clearing the other cell, which
        moved a score onto the wrong person without anybody noticing.
      */}
      {readButEmpty.length > 0 ? (
        <p className="mt-3 text-[13px] text-rust">
          {readButEmpty.length === 1 ? `Game ${readButEmpty[0]} has` : `Games ${readButEmpty.join(', ')} have`} writing
          on the card but nothing here. Fill {readButEmpty.length === 1 ? 'it' : 'them'} in from the card before saving.
        </p>
      ) : null}

      {!disabled && empty.length > 0 ? <p className="mt-3 text-[13px] text-rust">{emptyGamesMessage(empty)}</p> : null}

      {problems.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-1">
          {problems.map(({ game, scorers }) => (
            <li key={game.gameNumber} className="text-[13px] text-rust">
              Game {game.gameNumber} has scores for {scorers.map((p) => p.label).join(' and ')}. Only one player
              scores a game — unless it was a wall game, when everyone records {WALL_GAME_POINTS}.
            </li>
          ))}
        </ul>
      ) : null}

      {/* Nothing to add to a card she cannot change — owner, 2026-10-04. */}
      {disabled ? null : canAddGame ? (
        <button
          type="button"
          onClick={() =>
            onChange(addGame)
          }
          /* `max-w-[34rem]` — the grid's width. See the note above the table. */
          className="mt-3 min-h-[44px] w-full max-w-[34rem] rounded-md border border-dashed border-stroke text-[14px] text-ink-2"
        >
          + Game {Math.max(0, ...games.map(g => g.gameNumber)) + 1}
        </button>
      ) : (
        <p className="mt-3 text-center text-[12px] text-ink-3">
          {GAMES_MAX} games is the most a table plays in a session.
        </p>
      )}
    </fieldset>
  );
}

/**
 * "-10" from "10", "10" from "-10" — and a lone "-" from an empty box or a 0, ready for the number
 * (a "-0" helps nobody).
 */
export function flipSign(raw: string): string {
  const value = raw.trim();
  if (value.startsWith('-')) return value.slice(1);
  if (value === '' || Number(value) === 0) return '-';
  return `-${value}`;
}

/**
 * THE ± — owner, 2026-10-15: "-10 is a possibility", and the iPhone number pad has no minus key.
 *
 * INSIDE THE BOX SHE IS TYPING IN, LEFT OF THE NUMBER (owner's choice). Not beside Save: on an
 * iPhone the Save bar sits behind the open keyboard.
 *
 * ⚠ THE TOUCH IS CAUGHT ON `touchstart`, NOT `click`. On an iPhone a tap on a button takes the
 * focus out of the box first: the keyboard drops, the page jumps, and this button is gone before
 * the tap lands ("when you click on it the screen moves", owner, 2026-10-15). Stopping the touch
 * at its start keeps the box focused and the keyboard up. React registers touch listeners as
 * passive, which cannot stop anything — hence the listener added by hand.
 */
function SignButton({ signKey, negative, label, gameNumber, onFlip, onLeave }: {
  signKey: string;
  negative: boolean;
  label: string;
  gameNumber: number;
  onFlip: () => void;
  onLeave: () => void;
}) {
  const button = useRef<HTMLButtonElement>(null);
  const flip = useRef(onFlip);
  flip.current = onFlip;
  useEffect(() => {
    const el = button.current;
    if (!el) return;
    const touch = (event: TouchEvent) => {
      event.preventDefault();
      flip.current();
    };
    el.addEventListener('touchstart', touch, { passive: false });
    return () => el.removeEventListener('touchstart', touch);
  }, []);
  return (
    <button
      ref={button}
      type="button"
      data-sign={signKey}
      aria-label={negative ? `Make ${label}'s game ${gameNumber} score positive` : `Make ${label}'s game ${gameNumber} score negative`}
      onMouseDown={(event) => event.preventDefault()}
      onBlur={onLeave}
      onClick={() => flip.current()}
      className="absolute left-1 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full border border-stroke bg-paper text-[14px] font-semibold leading-none text-ink"
    >
      ±
    </button>
  );
}
